import {Injectable, OnDestroy} from '@angular/core';
import {Observable} from 'rxjs';
import {share, takeUntil} from 'rxjs/operators';
import {destroy$} from '../destroyable';

export interface TypedResizeObserverEntry<T extends Element> extends ResizeObserverEntry {
    readonly target: T;
}

type ListenerCallback<T extends Element> = (entry: TypedResizeObserverEntry<T>) => void;

type ListenerUnobserve = () => void;

interface ListenerRec<T extends Element> {
    resizeCallback: ListenerCallback<T>;
    unobserve: ListenerUnobserve;
}

/**
 * Sharable {@link ResizeObserver} with Observable interface.
 * It looks like reusing (one) the ResizeObserver is preferred and more performant:
 * https://github.com/WICG/ResizeObserver/issues/59#issuecomment-408098151
 * https://groups.google.com/a/chromium.org/forum/#!msg/blink-dev/z6ienONUb5A/F5-VcUZtBAAJ
 */
@Injectable()
export class ResizeObserverService implements OnDestroy {

    public readonly destroy$ = destroy$();

    private resizeObserver = new ResizeObserver(entries => this.handleResizeEvent(entries));

    private listenersMap = new WeakMap<Element, Array<ListenerRec<Element>>>();

    public ngOnDestroy(): void {
        this.resizeObserver.disconnect();
        this.resizeObserver = undefined;
        this.listenersMap = undefined;
    }

    public observe<T extends Element>(element: T): Observable<TypedResizeObserverEntry<T>> {
        return new Observable<TypedResizeObserverEntry<T>>(subscriber =>
            this.observeWithCallback(
                element,
                entry => subscriber.next(entry)
            )
        ).pipe(
            takeUntil(this.destroy$),
            share()
        );
    }

    /**
     * Note: Calling the unobserve is up to the caller.
     */
    public observeWithCallback<T extends Element>(element: T, callback: ListenerCallback<T>): ListenerUnobserve {
        const listenerRec = this.createListenerRec(element, callback);
        let listenerRecs = this.listenersMap.get(element);

        if (!listenerRecs) {
            listenerRecs = [listenerRec as ListenerRec<Element>];
            this.listenersMap.set(element, listenerRecs);
            this.resizeObserver.observe(element);
        } else {
            listenerRecs.push(listenerRec as ListenerRec<Element>);
        }

        return listenerRec.unobserve;
    }

    private handleResizeEvent(entries: ResizeObserverEntry[]): void {
        for (const entry of entries) {
            const listenerRecs = this.listenersMap.get(entry.target);

            if (listenerRecs) {
                for (const listenerRec of listenerRecs) {
                    listenerRec.resizeCallback(entry);
                }
            }
        }
    }

    private unobserve<T extends Element>(element: T, listenerRec?: ListenerRec<T>): void {
        const listenerRecs = this.listenersMap && this.listenersMap.get(element);

        if (listenerRecs) {
            if (listenerRec) {
                const iListenerRec = listenerRecs.indexOf(listenerRec as ListenerRec<Element>);

                if (iListenerRec >= 0) {
                    if (listenerRecs.length > 1) {
                        listenerRecs.splice(iListenerRec, 1);
                    } else {
                        this.unobserveElementForAll(element);
                    }
                }
            } else {
                this.unobserveElementForAll(element);
            }
        }
    }

    private unobserveElementForAll<T extends Element>(element: T): void {
        this.resizeObserver.unobserve(element);
        this.listenersMap.delete(element);
    }

    private createListenerRec<T extends Element>(element: T, callback: ListenerCallback<T>): ListenerRec<T> {
        const listenerRec: ListenerRec<T> = {
            resizeCallback: callback,
            unobserve:      () => this.unobserve(element, listenerRec)
        };

        return listenerRec;
    }

}
